import { createContext, PropsWithChildren, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MeetingResponse } from 'api/meedioBackend/types';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useLocalStorage from 'hooks/useLocalStorage/useLocalStorage';
import useWorkspacePermissions from 'hooks/useWorkspacePermissions/useWorkspacePermissions';
import { WorkspacePermissionsState } from 'hooks/useWorkspacePermissions/utils';
import { RoleAssignment, WorkspaceRole } from 'modules/CreateWorkspaceModal/types';
import constants from 'utils/Constants';
import logger from 'utils/logging/faro';
import { waitForRoomMembers } from 'utils/matrixUtils';
import { SetState } from 'utils/types';

import useBackendGuestMeetingRoomIds from './useBackendGuestMeetingRoomIds';
import useInvitedSpaces from './useInvitedSpaces';
import useWorkspaceRoles from './useWorkspaceRoles';
import useWorkspacesState from './useWorkspacesState';
import { LoadingMembersStatusEnum } from './utils';
import { WorkspacesState } from '../WorkspacesDropdown/utils';

export type WorkspacesContextType = {
  workspaceId: string | null;
  setWorkspaceId: SetState<string | null>;
  workspacesState: WorkspacesState;
  workspaceAvatarUrls: Record<string, string>;
  setWorkspaceAvatarUrl: (roomId: string, url: string) => void;
  clearWorkspaceAvatarUrl: (roomId: string) => void;
  personalWorkspaceName: string;
  workspaceRoles: WorkspaceRole[];
  roleAssignments: RoleAssignment[];
  invitedSpaces: string[];
  setInvitedSpaces: SetState<string[]>;
  backendGuestMeetingRoomIds: ReadonlySet<string>;
  backendGuestMeetings: ReadonlyArray<MeetingResponse>;
  backendGuestLoaded: boolean;
  workspacePermissions: WorkspacePermissionsState;
  loadingMembersStatus: LoadingMembersStatusEnum;
  setLoadingMembersStatus: SetState<LoadingMembersStatusEnum>;
};

export const WorkspacesContext = createContext<WorkspacesContextType | null>(null);

const WorkspacesProvider = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation();
  const { matrixClient, hasReachedSyncReadyState } = useMatrixContext({ requireDefinedMatrixClient: false });
  const [workspaceId, setWorkspaceId] = useLocalStorage<string | null>(constants.WORKSPACE_ID, null);
  const workspacesState = useWorkspacesState(workspaceId, setWorkspaceId);
  const [workspaceAvatarUrls, setWorkspaceAvatarUrls] = useState<Record<string, string>>({});
  const [invitedSpaces, setInvitedSpaces] = useState<string[]>([]);
  const personalWorkspaceName = t('workspaces.personal');
  const { roles: workspaceRoles, roleAssignments } = useWorkspaceRoles(workspaceId);
  const workspacePermissions = useWorkspacePermissions(workspaceId);
  const [loadingMembersStatus, setLoadingMembersStatus] = useState(LoadingMembersStatusEnum.READY);

  const {
    roomIds: backendGuestMeetingRoomIds,
    meetings: backendGuestMeetings,
    isLoaded: backendGuestLoaded,
  } = useBackendGuestMeetingRoomIds(matrixClient ?? undefined, workspacesState.workspaces);

  useInvitedSpaces(setInvitedSpaces, backendGuestMeetingRoomIds, backendGuestLoaded);

  const setWorkspaceAvatarUrl = (roomId: string, url: string) =>
    setWorkspaceAvatarUrls((prev) => ({ ...prev, [roomId]: url }));

  const clearWorkspaceAvatarUrl = (roomId: string) =>
    setWorkspaceAvatarUrls((prev) => {
      const { [roomId]: _removed, ...rest } = prev;
      return rest;
    });

  useEffect(() => {
    if (!hasReachedSyncReadyState) return;
    if (!workspaceId || !matrixClient) {
      return setLoadingMembersStatus(LoadingMembersStatusEnum.READY);
    }

    const room = matrixClient.getRoom(workspaceId);
    if (!room) {
      return setLoadingMembersStatus(LoadingMembersStatusEnum.ERROR);
    }

    let cancelled = false;
    setLoadingMembersStatus(LoadingMembersStatusEnum.LOADING);

    waitForRoomMembers(room)
      .then(() => {
        if (cancelled) return;
        setLoadingMembersStatus(LoadingMembersStatusEnum.SUCCESS);
      })
      .catch((error) => {
        if (cancelled) return;
        setLoadingMembersStatus(LoadingMembersStatusEnum.ERROR);
        logger.warn('Workspace member pre-load failed', error);
      });

    return () => {
      cancelled = true;
    };
  }, [workspaceId, matrixClient, setLoadingMembersStatus, hasReachedSyncReadyState]);

  const state: WorkspacesContextType = {
    workspaceId,
    setWorkspaceId,
    workspacesState,
    workspaceAvatarUrls,
    setWorkspaceAvatarUrl,
    clearWorkspaceAvatarUrl,
    personalWorkspaceName,
    workspaceRoles,
    roleAssignments,
    invitedSpaces,
    setInvitedSpaces,
    backendGuestMeetingRoomIds,
    backendGuestMeetings,
    backendGuestLoaded,
    workspacePermissions,
    loadingMembersStatus,
    setLoadingMembersStatus,
  };

  return <WorkspacesContext.Provider value={state}>{children}</WorkspacesContext.Provider>;
};

export default WorkspacesProvider;
